import { useEffect } from 'react';

import { usePusherContext } from './PusherContext';
import { eventCreatorsByEventName, pusherBus, subscribeToPusherChannel } from './helpers';
import {
  CreatePusherEventOptions,
  PusherChannel,
  PusherEvent,
  PusherEventCreator,
  PusherEventMetaData,
  PusherEventName,
} from './types';

export const usePusherChannel = (
  userId: MongoId | undefined,
  channelIdentifier: Parameters<typeof subscribeToPusherChannel>[2] | null
) => {
  const { pusherRef } = usePusherContext();

  useEffect(
    function init() {
      if (!userId) return;
      if (!pusherRef.current) return;
      if (!channelIdentifier) return;

      const unsubscribe = subscribeToPusherChannel(userId, pusherRef.current, channelIdentifier);

      return function cleanup() {
        unsubscribe();
      };
    },
    [userId, channelIdentifier]
  );
};

export const usePusherEvent = <EventName extends PusherEventName, Payload extends {}>(
  eventCreator: PusherEventCreator<Payload, EventName>,
  callback: (event: PusherEvent<Payload, PusherEventName>) => void
) => {
  useEffect(
    function subscribeToPusherBus() {
      const unsubscribe = pusherBus.subscribe((event) => {
        if (event.type !== eventCreator.eventName) return;
        callback(event);
      });

      return function cleanup() {
        unsubscribe();
      };
    },
    [eventCreator.eventName, callback]
  );
};

export const createPusherEvent = <P extends {} = never, EventName extends PusherEventName = PusherEventName>(
  eventName: EventName,
  options: CreatePusherEventOptions
) => {
  const meta: PusherEventMetaData = {
    notify: options.notify ?? true,
    ignoreForSameUser: options.ignoreForSameUser ?? true,
    receivedOn: options.channels[0],
    eventName,
  };

  const eventCreator: PusherEventCreator<P, EventName> = (payload: P, channel: PusherChannel) => {
    return {
      type: eventName,
      payload,
      meta: {
        ...meta,
        receivedOn: channel,
      },
    };
  };

  eventCreator.eventName = eventName;
  eventCreator.channels = options.channels;

  eventCreator.toString = () => eventName;
  eventCreator.match = (event): event is PusherEvent<P, EventName> => event.type === eventName;

  /**
   * Store it to a map so that after pusher subscription
   * subscriber can create the event
   */
  eventCreatorsByEventName[eventName] = eventCreator;

  return eventCreator;
};
